import React, { Fragment, Component } from "react";
import { bindActionCreators } from "redux";
import { show, hide } from "redux-modal";
import styled from "styled-components";
import PropTypes from "prop-types";
import {
  Thirds,
  Left,
  Right,
  Center,
  Button,
  Frame,
  Search,
  DisplayMedium,
  spacing,
} from "@conserveit/blocks";
import Helmet from "react-helmet";
import { connect } from "react-redux";
import { MsgAlert } from "../../components";
import * as groupsActions from "../../redux/modules/groups/actionCreators";
import { GroupGrid } from "./components/GroupGrid";
import EditGroupModal, { EDIT_GROUP_MODAL } from "./components/EditGroupModal";
import AddGroupModal, { ADD_GROUP_MODAL } from "./components/AddGroupModal";

const StyledTitle = styled(DisplayMedium)`
  text-align: center;
  margin-bottom: ${spacing.loose};
`;

const StyledThirds = styled(Thirds)`
  margin-bottom: ${spacing.default};
`;

function makeSearchable(groups) {
  return groups.map((group) => {
    const targetValueString = Object.values(group).join(" ").replace(/,/g, " ");
    return {
      ...group,
      targetValueString,
    };
  });
}

const SEARCH_KEY = "insightsSearch";
// replace with dynamic list
const groupTypeList = {
  project: "Project",
  site: "Site",
  group: "Responsible",
};

@connect(
  (state) => ({
    groups: state.groups.groups,
    user: state.auth.user,
    adding: state.groups.adding,
    editing: state.groups.editing,
    working: state.groups.working,
    project_added: state.groups.project_added,
    modal_data: state.groups.modal_data,
    group_form_data: state.groups.group_form_data,
    error: state.groups.error,
    success: state.groups.success,
    projects: state.userSites.projects,
    activeModal: state.betaModal,
  }),
  (dispatch) => ({
    ...bindActionCreators(
      {
        ...groupsActions,
      },
      dispatch
    ),
    clearForm: () => {
      dispatch(groupsActions.handleClose());
    },
    closeModal: (modalName) => {
      dispatch(hide(modalName));
    },
    handleAddGroupFormOpen: () => {
      dispatch(groupsActions.startAddGroup());
      dispatch(show(ADD_GROUP_MODAL));
    },
    handleEditGroupFormOpen: (group) => {
      dispatch(groupsActions.startEditGroup(group));
      dispatch(show(EDIT_GROUP_MODAL));
    },
  })
)
class GroupAdmin extends Component {
  static propTypes = {
    loading: PropTypes.bool,
    loaded: PropTypes.bool,
    adding: PropTypes.bool,
    editing: PropTypes.bool,
    working: PropTypes.bool,
    project_added: PropTypes.bool,

    groups: PropTypes.array.isRequired,
    projects: PropTypes.object,
    error: PropTypes.string,
    success: PropTypes.string,
    modal_data: PropTypes.object,
    group_form_data: PropTypes.object,
    activeModal: PropTypes.object,
    loadGroups: PropTypes.func.isRequired,
    handleAddGroupFormOpen: PropTypes.func.isRequired,
    handleEditGroupFormOpen: PropTypes.func.isRequired,
    handleClose: PropTypes.func.isRequired,
    handleChange: PropTypes.func.isRequired,
    addGroupRequest: PropTypes.func.isRequired,
    saveGroupRequest: PropTypes.func.isRequired,
    clearForm: PropTypes.func.isRequired,
    closeModal: PropTypes.func.isRequired,
  };
  state = {
    searchableGroups: [],
    filteredGroups: [...this.props.groups],
    query: undefined,
  };
  componentDidMount() {
    this.props.loadGroups(this.props.user);
  }
  componentDidUpdate({ groups, success }) {
    if (groups !== this.props.groups) {
      this.setInitialGroups(this.props.groups);
    }

    if (!success && this.props.success) {
      this.handleModalClose();
    }
  }

  setInitialGroups = (groups) => {
    this.setState({
      searchableGroups: makeSearchable(groups),
      filteredGroups: this.props.groups,
    });
  };
  handleModalClose = () => {
    const activeModalName = Object.keys(this.props.activeModal)[0];
    this.props.clearForm();
    this.props.closeModal(activeModalName);
  };

  handleSearch = ({ filtered, query }) => {
    this.setState({
      filteredGroups: filtered,
      query,
    });
  };

  formatProjectList = (projects) =>
    Object.values(projects).reduce((keyLabel, project) => {
      keyLabel[project.jira_key] = project.project_name;
      return keyLabel;
    }, {});

  render() {
    const {
      groups,
      handleAddGroupFormOpen,
      handleEditGroupFormOpen,
      handleChange,
      projects,
      addGroupRequest,
      saveGroupRequest,
      modal_data: modalData,
      group_form_data: groupFormData,
      editing,
      error,
      success,
    } = this.props;

    const projectList = this.formatProjectList(projects);

    return (
      <Fragment>
        <Helmet title="Administration" />
        <StyledTitle>Manage Groups</StyledTitle>
        <StyledThirds>
          <Left>
            <Button
              additive
              thin
              onClick={handleAddGroupFormOpen}
              data-cy="GroupAdmin-openGroupModal"
            >
              Add Group
            </Button>
          </Left>
          <Center>
            <Search
              sessionKey={SEARCH_KEY}
              initialSearch={this.state.query}
              data={this.state.searchableGroups}
              onSearch={this.handleSearch}
              defaultField="targetValueString"
              searchFields={["group_key", "group_name", "group_type"]}
              indexId="group_id"
              placeholder="Search Groups"
              thick
              cy="Groups-Search"
            />
          </Center>
          <Right />
        </StyledThirds>
        <MsgAlert type="success" message={success} />
        <Frame>
          {groups.length > 0 && (
            <GroupGrid
              groups={this.state.filteredGroups}
              groupTypeList={groupTypeList}
              projects={projects}
              onRowSelect={handleEditGroupFormOpen}
            />
          )}
        </Frame>
        <AddGroupModal
          title={modalData.title}
          submitText={modalData.submitText}
          handleClose={this.handleModalClose}
          groupFormData={groupFormData}
          groupTypeList={groupTypeList}
          projectList={projectList}
          handleChange={handleChange}
          error={error}
          editing={editing}
          addGroupRequest={addGroupRequest}
        />

        {/* View/Edit User Modal */}
        <EditGroupModal
          title={modalData.title}
          submitText={modalData.submitText}
          handleClose={this.handleModalClose}
          groupFormData={groupFormData}
          groupTypeList={groupTypeList}
          projectList={projectList}
          handleChange={handleChange}
          error={error}
          editing={editing}
          saveGroupRequest={saveGroupRequest}
        />
      </Fragment>
    );
  }
}

export default GroupAdmin;
